/*
 * Copyright (c) 2020 wlocalhost.org.
 * All rights reserved.
 */

import { IBackground, IBorder, IFont, ILineHeight, IMargin, IPadding, IWidthHeight, TFontWeight } from './interfaces/interfaces';
import { defer, Observable, of } from 'rxjs';

// import { getDiff } from 'recursive-diff';

/**
 * An alternative lodash cloneDeep function. Use it on your own risk.
 * @param obj Object to be cloned.
 */
export function cloneDeep<T>(obj = {}): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * An alternative lodash isEqual function. Use it on your own risk.
 * @param x Object to be compared
 * @param y the second Object to be compared
 */
export function isEqual(x: object, y: object): boolean {
  // return !getDiff(x, y).length;
  return JSON.stringify(x) === JSON.stringify(y);
}

/**
 * An alternative lodash defaultsDeep function. Use it on your own risk.
 * @param to The object with default properties
 * @param sources An array of objects with default properties
 */
export function defaultsDeep<T>(to: T = {} as T, ...sources: T[]): T {
  for (const source of sources) {
    for (const key in source) {
      if (to.hasOwnProperty(key)) {
        continue;
      }
      if (!Array.isArray(source[key]) && typeof source[key] === 'object') {
        to[key] = defaultsDeep(to[key], source[key]);
      } else {
        to[key] = source[key];
      }
    }
  }
  return (to as unknown) as T;
}

/**
 * Create CSS border styles based on {@link IBorder} object.
 * @param border Border object.
 * @param rule Most likely you won't need it, but for some cases, it can be changed. Default is: `border`.
 *
 * @return CSS Border styles.
 */
export function createBorder(border: IBorder, rule = 'border'): { [p: string]: string; borderRadius: string } {
  const { color = '#000000', style = 'solid', width = 4, radius = 0 } = border;
  return {
    [rule]: `${width}px ${style} ${color}`,
    borderRadius: `${radius}px`
  };
}

/**
 * Create CSS padding styles based on {@link IPadding} object.
 * @param padding Padding object.
 * @param rule Most likely you won't need it, but for some cases, it can be changed. Default is: `padding`.
 */
export function createPadding(padding: IPadding, rule = 'padding'): { [p: string]: string } {
  const { top = 10, right = 25, bottom = 10, left = 25 } = padding;
  return {
    [rule]: `${top}px ${right}px ${bottom}px ${left}px`
  };
}

/**
 * Create CSS margin styles based on {@link IMargin} object.
 * @param margin Margin object.
 */
export function createMargin(margin: IMargin): { margin: string } {
  const { top = 0, bottom = 0 } = margin;
  return {
    margin: `${top}px 0 ${bottom}px`
  };
}

/**
 * Create CSS font styles based on {@link IFont} object.
 * @param font Font object.
 */
export function createFont(font: IFont): { fontFamily: string; fontSize: string; fontStyle: string; fontWeight: number | TFontWeight } {
  const { family = '', size = 13, style = 'normal', weight = 'normal' } = font;
  return {
    fontFamily: family,
    fontSize: `${size}px`,
    fontStyle: style,
    fontWeight: weight
  };
}

/**
 * Create CSS line-height styles based on {@link ILineHeight} object.
 * @param lineHeight Line-height object.
 */
export function createLineHeight(lineHeight: ILineHeight): { lineHeight: string } {
  const { value = 22, unit = 'px' } = lineHeight;
  return {
    lineHeight: unit !== 'none' ? `${value}${unit}` : 'normal'
  };
}

/**
 * Create background styles based on {@link IBackground} object.
 * @param background Background object.
 */
export function createBackground(background: IBackground): string {
  const { url = '', color = 'white', repeat = 'no-repeat' } = background;
  return `${color} ${url && `url(${url})`} ${repeat} top center`;
}

/**
 * Create Width or Height styles based on {@link IWidthHeight} object.
 * @param widthHeight Width or Height object.
 */
export function createWidthHeight(widthHeight: IWidthHeight): string {
  const { value = 100, unit = '%', auto = false } = widthHeight;
  return (auto && 'auto') || (['%', 'px'].indexOf(unit) > -1 && `${value}${unit}`) || unit;
}

/**
 * Convert bytes to KB-YB.
 * @param bytes Bytes number.
 * @param decimals How many decimals to be used. Default: 2.
 */
export function bytesToSize(bytes: number, decimals = 2): string {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i];
}

/**
 * Get a Social Icon path.
 * @param path Icon path. Ex: facebook.png
 */
export function getAssetByPath(path: string): string {
  return `https://www.mailjet.com/images/theme/v1/icons/ico-social/${path}`;
}

/**
 * A simple and powerful function that allows to create an `Observable` only when it needs to be used.
 * @param args Arguments to send to new Observable.
 */
export function deferOf<T>(...args: T[]): Observable<T> {
  return defer(() => of(...args));
}
